import { ReportContentActions } from 'app/editor/report';
import React, { Component, useEffect } from 'react';
import { alpha, Box, Stack } from '@mui/material';
import { BlockType, ComponentSettings, ContentSettings, DatasetField } from '@report-content';
import DatasetComponentBuilder from 'components/report-content/components/dataset/DatasetComponentBuilder';
import PivotComponentBuilder from 'components/report-content/components/pivot/PivotComponentBuilder';
import LineChartComponentBuilder from 'components/report-content/components/line-chart/LineChartComponentBuilder';
import ExternalVisualizationComponentBuilder from 'components/report-content/components/external-visualization/ExternalVisualizationComponentBuilder';
import ManageFiltersComponentBuilder from 'components/report-content/components/manage-filters/ManageFiltersComponentBuilder';
import PieChartComponentBuilder from 'components/report-content/components/pie-chart/PieChartComponentBuilder';
import BubbleChartComponentBuilder from 'components/report-content/components/bubble-chart/BubbleChartComponentBuilder';
import FunnelChartComponentBuilder from 'components/report-content/components/funnel-chart/FunnelChartComponentBuilder';
import RangeChartComponentBuilder from 'components/report-content/components/range-chart/RangeChartComponentBuilder';
import MapChartComponentBuilder from 'components/report-content/components/map-chart/MapChartComponentBuilder';
import TextBlockComponentBuilder from 'components/report-content/components/text-block/TextBlockComponentBuilder';
import BlockTopControls from 'components/report-content/block-top-controls/BlockTopControls';
import JctComponentBuilder from 'components/report-content/components/jct/JctComponentBuilder';
import KpiChartComponentBuilder from 'components/report-content/components/kpi-chart/KpiChartComponentBuilder';
import LlmComponentBuilder from './components/llm/LlmComponentBuilder';

const BUILDER_MAP: Record<string, new (...args: any[]) => any> = {
    dataset: DatasetComponentBuilder,
    pivot: PivotComponentBuilder,
    line: LineChartComponentBuilder,
    kpi_chart: KpiChartComponentBuilder,
    chart: PieChartComponentBuilder,
    bubble_chart: BubbleChartComponentBuilder,
    funnel_chart: FunnelChartComponentBuilder,
    range_chart: RangeChartComponentBuilder,
    map_chart: MapChartComponentBuilder,
    text_block: TextBlockComponentBuilder,
    external_visualization: ExternalVisualizationComponentBuilder,
    llm: LlmComponentBuilder,
    manage_filters: ManageFiltersComponentBuilder,
};
export default function ReportContentBlock({
    contentSettings,
    block,
    actions,
    isFilterBlock,
    datasetFields,
}: {
    contentSettings: ContentSettings;
    block: BlockType<ComponentSettings>;
    actions: ReportContentActions;
    isFilterBlock?: boolean;
    datasetFields: Array<DatasetField>;
}) {
    let component: any = 'ComponentNotFound: ' + block.component.internalName;
    let editPanel: any = 'ComponentNotFound: ' + block.component.internalName;
    let title: string = block.component.settings?.title?.length ? block.component.settings.title : '';
    let componentControls = null;

    const updateBlockSettings = (newSettings: any, options?: { shouldDirty?: boolean }) => {
        actions.updateBlockSettings(block.uid, newSettings, options);
    };

    const jct = block.component.internalName.match(/jct_([0-9]+)/);
    const BuilderClass = jct ? JctComponentBuilder : BUILDER_MAP[block.component.internalName];
    const builder = BuilderClass
        ? new BuilderClass(contentSettings, block, datasetFields, updateBlockSettings, actions)
        : undefined;

    if (builder) {
        editPanel = builder.getEditPanel();
        componentControls = builder.getComponentControls();
        if (block.component.settings.isOnCreation === true) {
            component = <NotConfirmedComponent block={block} actions={actions} editPanel={editPanel} />;
        } else {
            component = builder.getComponent(editPanel);
        }
    }
    component = <ComponentErrorBoundary>{component}</ComponentErrorBoundary>;

    //ToDO Stas: css fixed/sticky style and add ability to set position
    if (isFilterBlock) {
        return (
            <span
                id={`report_block_${block.component.datasetRendererBlockComponentId}`}
                data-test={`report-block-filter${block.component.datasetRendererBlockComponentId}`}
            >
                {component}
                {contentSettings.context == 'edit' && <Box sx={{ flexShrink: 0, p: 0.75 }}>{componentControls}</Box>}
            </span>
        );
    }

    // Component renders Title by itself.
    const isInnerTitle = block.component.internalName == 'kpi_chart';

    return (
        <Box id={`report_block_${block.component.datasetRendererBlockComponentId}`}>
            {title.length > 0 &&
                !contentSettings.forScreenShot &&
                ((contentSettings.context == 'view' && !isInnerTitle) ||
                    (contentSettings.context == 'edit' && block.component.settings.is_collapsed_ind == 'N')) && (
                    <Stack
                        direction={'row'}
                        justifyContent={'center'}
                        sx={{ textAlign: 'center', fontSize: '16px', fontWeight: 500, pb: 1 }}
                    >
                        {title}
                    </Stack>
                )}
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                    borderRadius: contentSettings.context == 'edit' ? '0 1px 1px 0' : 1,
                }}
            >
                {contentSettings.context == 'edit' && (
                    <BlockTopControls
                        editPanel={editPanel}
                        actions={actions}
                        add={componentControls}
                        block={block}
                        datasetFields={datasetFields}
                    />
                )}
                <Box
                    data-test={`report-block-${block.component.internalName}`}
                    className={
                        block.component.settings.is_collapsed_ind == 'Y' && contentSettings.context == 'edit'
                            ? 'd-none'
                            : ''
                    }
                >
                    {component}
                </Box>
            </Box>
        </Box>
    );
}

function NotConfirmedComponent({
    block,
    actions,
    editPanel,
}: {
    block: BlockType<ComponentSettings>;
    actions: ReportContentActions;
    editPanel: any;
}) {
    useEffect(() => {
        actions.openBlockEditor(block.uid, editPanel);
    }, []);

    return <div>Component not confirmed</div>;
}

class ComponentErrorBoundary extends Component<any, any> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <div>Report Component is broken =(</div>;
        }
        return this.props.children;
    }
}
